/**
 * PATH: apps/ai/src/config/navigation.ts
 * PURPOSE: AI Hub Navigation Configuration
 * 
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  LayoutDashboard,
  Bot,
  Lightbulb,
  FileSearch,
  Calculator,
  MessageSquare,
  BarChart,
  Settings,
  Briefcase,
  FileEdit,
  Mail,
  GitCompare,
  Plus,
  Sparkles,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'ai' as HubId,
  name: 'AI Hub',
  icon: Bot,
  accentColor: 'cyan',
  accentGradient: 'from-cyan-500 to-blue-600',
  baseUrl: 'https://ai.quotech.ai',
}

// =============================================================================
// MAIN NAVIGATION (Core Work)
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'chat',
    label: 'Chat',
    icon: MessageSquare,
    href: '/chat',
  },
  {
    id: 'skills',
    label: 'AI Skills',
    icon: Sparkles,
    href: '/skills',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: FileEdit,
    href: '/reports',
  },
  {
    id: 'assistant',
    label: 'Quick Actions',
    icon: Lightbulb,
    href: '/assistant',
  },
  {
    id: 'extraction',
    label: 'Extraction',
    icon: FileSearch,
    href: '/extraction',
  },
  {
    id: 'suggestions',
    label: 'Suggestions',
    icon: Lightbulb,
    href: '/suggestions',
  },
  {
    id: 'calculations',
    label: 'Calculations',
    icon: Calculator,
    href: '/calculations',
  },
  {
    id: 'similarity',
    label: 'Similarity',
    icon: GitCompare,
    href: '/similarity',
  },
]

// =============================================================================
// SECONDARY NAVIGATION (Tools)
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('ai')

// =============================================================================
// QUICK ADD ITEMS (Header dropdown)
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'ask-ai',
    label: 'Ask AI',
    icon: Bot,
    action: () => { window.location.href = '/chat' },
    shortcut: '⌘⇧A',
  },
  {
    id: 'new-extraction',
    label: 'New Extraction',
    icon: FileSearch,
    action: () => { window.location.href = '/extraction' },
  },
  {
    id: 'find-similar',
    label: 'Find Similar',
    icon: GitCompare,
    action: () => { window.location.href = '/similarity' },
  },
]

// =============================================================================
// COMMAND PALETTE COMMANDS
// =============================================================================

export const COMMANDS: CommandItem[] = [
  // Actions
  {
    id: 'ask-ai',
    label: 'Ask AI Assistant',
    description: 'Ask the AI assistant a question',
    icon: Bot,
    action: () => { window.location.href = '/chat' },
    keywords: ['ask', 'ai', 'assistant', 'help', 'chat'],
    category: 'action',
  },
  {
    id: 'new-extraction',
    label: 'New Extraction',
    description: 'Extract data from a document',
    icon: FileSearch,
    action: () => { window.location.href = '/extraction' },
    keywords: ['extract', 'document', 'pdf', 'data'],
    category: 'action',
  },
  {
    id: 'get-suggestions',
    label: 'Get Suggestions',
    description: 'Get AI-powered suggestions',
    icon: Lightbulb,
    action: () => { window.location.href = '/suggestions' },
    keywords: ['suggest', 'recommend', 'ideas'],
    category: 'action',
  },
  
  // Navigation
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    icon: LayoutDashboard,
    action: () => { window.location.href = '/' },
    keywords: ['navigate', 'home', 'dashboard'],
    category: 'navigation',
  },
  {
    id: 'go-chat',
    label: 'Go to Chat',
    icon: Bot,
    action: () => { window.location.href = '/chat' },
    keywords: ['navigate', 'chat', 'assistant'],
    category: 'navigation',
  },
  {
    id: 'go-extraction',
    label: 'Go to Extraction',
    icon: FileSearch,
    action: () => { window.location.href = '/extraction' },
    keywords: ['navigate', 'extraction'],
    category: 'navigation',
  },
  {
    id: 'go-suggestions',
    label: 'Go to Suggestions',
    icon: Lightbulb,
    action: () => { window.location.href = '/suggestions' },
    keywords: ['navigate', 'suggestions'],
    category: 'navigation',
  },
  {
    id: 'go-calculations',
    label: 'Go to Calculations',
    icon: Calculator,
    action: () => { window.location.href = '/calculations' },
    keywords: ['navigate', 'calculations', 'estimate'],
    category: 'navigation',
  },
  {
    id: 'go-similarity',
    label: 'Go to Similarity Search',
    icon: GitCompare,
    action: () => { window.location.href = '/similarity' },
    keywords: ['navigate', 'similarity', 'search', 'find'],
    category: 'navigation',
  },
  {
    id: 'go-usage',
    label: 'Go to Dashboard',
    icon: BarChart,
    action: () => { window.location.href = '/dashboard' },
    keywords: ['navigate', 'dashboard', 'analytics', 'stats'],
    category: 'navigation',
  },
  {
    id: 'go-settings',
    label: 'Go to Settings',
    icon: Settings,
    action: () => { window.location.href = '/settings' },
    keywords: ['navigate', 'settings', 'config'],
    category: 'navigation',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-quote',
    label: 'Switch to Quote Builder',
    icon: FileEdit,
    action: () => { window.location.href = 'https://quote.quotech.ai' },
    keywords: ['hub', 'quote', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-email',
    label: 'Switch to Email Hub',
    icon: Mail,
    action: () => { window.location.href = 'https://email.quotech.ai' },
    keywords: ['hub', 'email', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS (for Command Palette)
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS

// =============================================================================
// LEGACY EXPORTS (for backwards compatibility)
// =============================================================================

export const navigationItems = NAV_ITEMS
export const moduleConfig = MODULE_CONFIG
